import { OrderMessageCard } from "../OrderDetails/OrderMessageCard";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlinePencil, HiX, HiCheck, HiOutlineMail } from "react-icons/hi";
import { Customer, Order } from "../../models";
import { OrderType } from "../../types/order/IOrder";
import { getOrderType, formatPrice } from "../../helpers/orderFormat";
import { Button } from "../Button/Button";
import { CopyButton } from "../Button/CopyButton";
import { RouteTimeline } from "../RouteTimeline/RouteTimeline";
import {
  cardStat,
  sectionDivider,
  textBody,
  textHeading,
} from "@design-system";

export interface OrderPricingData {
  amounts: {
    totalAmount: number;
    totalAmountWithoutVat: number;
    totalVatAmount: number;
  };
  items: any[];
}

interface ContactCopyCardProps {
  label: string;
  value: string;
}

const ContactCopyCard: FC<ContactCopyCardProps> = ({ label, value }) => (
  <div className={`flex-1 ${cardStat}`}>
    <div className={`text-sm font-medium ${textHeading} mb-1`}>{label}</div>
    <div className="flex items-center justify-between group">
      <span className={`text-sm ${textBody}`}>{value}</span>
      <CopyButton value={value} />
    </div>
  </div>
);

export interface IncomingOrderDetailsProps {
  order: Order | null;
  pricingData: OrderPricingData | null;
  isPriceLoading: boolean;
  isRejecting: boolean;
  isConfirming: boolean;
  onEditOrder: () => void;
  onRejectOrder: () => void;
  onApproveOrder: () => void;
}

/**
 * Detail panel rendered beside the incoming-orders table. Shows the selected
 * order's customer header + actions, contact/price cards, message, route
 * timeline and the price breakdown.
 */
export const IncomingOrderDetails: FC<IncomingOrderDetailsProps> = ({
  order,
  pricingData,
  isPriceLoading,
  isRejecting,
  isConfirming,
  onEditOrder,
  onRejectOrder,
  onApproveOrder,
}) => {
  const { t } = useTranslation(["common", "orders"]);

  if (!order) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted">{t("orders:select_order_to_view_details")}</p>
      </div>
    );
  }

  const customerName =
    Customer.getCustomerName(order.customer) || t("common:unknown_customer");

  const orderType = getOrderType(order);
  const isOrderPriceRequest = orderType === OrderType.PriceRequest;

  return (
    <div>
      {/* Header with Customer Info and Action Buttons */}
      <div className={`${sectionDivider} pb-4 mt-4 border-t-0 border-b`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-text">{customerName}</h2>
          </div>
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              onClick={onEditOrder}
              icon={<HiOutlinePencil className="w-4 h-4" />}
            >
              {t("common:edit")}
            </Button>
            <Button
              variant="danger"
              onClick={onRejectOrder}
              disabled={isRejecting}
            >
              {isRejecting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("common:rejecting", "Rejecting...")}
                </>
              ) : (
                <>
                  <HiX className="w-4 h-4 mr-2" />
                  {t("orders:reject")}
                </>
              )}
            </Button>
            <Button
              variant="success"
              onClick={onApproveOrder}
              disabled={isConfirming}
            >
              {isConfirming ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("common:confirming", "Confirming...")}
                </>
              ) : (
                <>
                  <HiCheck className="w-4 h-4 mr-2" />
                  {t("orders:approve")}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Contact and Price Information */}
      <div className="flex space-x-4 mt-4">
        {order.customer?.contactEmail && (
          <ContactCopyCard
            label={t("common:email")}
            value={order.customer.contactEmail}
          />
        )}
        {order.customer?.phoneNumber && (
          <ContactCopyCard
            label={t("common:phone")}
            value={order.customer.phoneNumber}
          />
        )}
        {!isOrderPriceRequest && pricingData?.amounts && (
          <div className={`flex-1 ${cardStat}`}>
            <div className="text-sm font-medium text-text mb-1">
              {t("common:total_price")}
            </div>
            <div className="text-base font-semibold text-text">
              {formatPrice(pricingData.amounts.totalAmount)}
            </div>
          </div>
        )}
      </div>

      {/* Message Box */}
      {order.comment && (
        <OrderMessageCard
          message={order.comment}
          projectNumber={order.projectNumber}
        >
          {order.customer?.contactEmail && (
            <div className="mt-4 flex justify-end">
              <Button
                onClick={() =>
                  (window.location.href = `mailto:${order.customer?.contactEmail}`)
                }
                variant="secondary"
                size="sm"
                icon={<HiOutlineMail />}
              >
                {t("common:reply_by_email")}
              </Button>
            </div>
          )}
        </OrderMessageCard>
      )}

      {/* Route Information */}
      <div className="mt-6 mb-6">
        {order.route && (
          <RouteTimeline stops={order.route} cargo={order.cargo} />
        )}
      </div>

      {/* Price Summary */}
      {order.cargo && order.cargo.length > 0 && (
        <div className="mt-4">
          <div className={`${cardStat} p-4`}>
            <h3 className="text-base font-semibold text-text mb-3">
              {t("common:price_breakdown")}
            </h3>
            {isPriceLoading ? (
              <p className="text-sm text-muted">{t("common:loading")}</p>
            ) : pricingData?.amounts ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted">
                    {pricingData.amounts.totalVatAmount > 0
                      ? t("common:netto")
                      : t("common:subtotal_excl_vat")}
                  </span>
                  <span className="text-text">
                    {formatPrice(pricingData.amounts.totalAmountWithoutVat)}
                  </span>
                </div>
                {pricingData.amounts.totalVatAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted">{t("common:vat")}</span>
                    <span className="text-text">
                      {formatPrice(pricingData.amounts.totalVatAmount)}
                    </span>
                  </div>
                )}
                <div
                  className={`flex justify-between font-semibold border-t border-border pt-2`}
                >
                  <span className="text-text">
                    {pricingData.amounts.totalVatAmount > 0
                      ? t("common:total_incl_vat")
                      : t("common:total")}
                  </span>
                  <span className="text-text">
                    {formatPrice(pricingData.amounts.totalAmount)}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted">-</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
